import React, { useState } from 'react';
import {
  Box,
  Collapse,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';

const GREEN = '#0F6E56';
const RED = '#993C1D';

const fmtNum = (v) => {
  if (v == null || !Number.isFinite(v)) return '—';
  return Math.round(v).toLocaleString('en-US');
};

const fmtPct = (v, budget) => {
  if (v == null || !Number.isFinite(v) || !budget) return '';
  return `${((v / budget) * 100).toFixed(1)}%`;
};

const fmtVariance = (v) => {
  if (v == null || !Number.isFinite(v))
    return { text: '—', color: 'text.secondary', value: null };
  const sign = v >= 0 ? '+' : '';
  return {
    text: `${sign}${Math.round(v).toLocaleString('en-US')}`,
    color: v >= 0 ? GREEN : RED,
    value: v,
  };
};

const fmtCostVariance = (v) => {
  const formatted = fmtVariance(v);
  if (v == null || !Number.isFinite(v)) return formatted;
  return {
    ...formatted,
    color: v > 0 ? RED : v < 0 ? GREEN : 'text.secondary',
  };
};

const columnWidths = ['38%', '12.4%', '12.4%', '12.4%', '12.4%', '12.4%'];

/**
 * Labor Hours summary table — matches the prototype layout.
 *
 * Rows: Project total budget, then Work Packages, General Conditions,
 * and Change Orders sections.
 * Columns: Period, Planned, Earned, Actual, Cost Var, Sched Var.
 *
 * Props:
 *   budgetTotal  — { workPackageHrs, generalConditionsHrs, changeOrderHrs, totalHrs }
 *   workPackages — { projectToDate, thisWeek, lastWeek }
 *   generalConditions — { projectToDate, thisWeek }
 *   changeOrders — { projectToDate, thisWeek }
 *   metrics       — { costVarianceOverall, schedVarianceOverall, costVarianceThisWeek, schedVarianceThisWeek }
 *   packageDrilldowns — package-level rows keyed by summary row
 */
const LaborHoursTable = ({
  budgetTotal,
  workPackages,
  generalConditions,
  changeOrders,
  projectToDate,
  thisWeek,
  lastWeek,
  metrics,
  packageDrilldowns = {},
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const projectBudget = budgetTotal?.totalHrs || 0;
  const workPackageBudget =
    budgetTotal?.workPackageHrs ?? budgetTotal?.totalHrs ?? 0;
  const generalConditionsBudget = budgetTotal?.generalConditionsHrs || 0;
  const changeOrderBudget = budgetTotal?.changeOrderHrs || 0;

  const workPackageProjectToDate = workPackages?.projectToDate || projectToDate;
  const workPackageThisWeek = workPackages?.thisWeek || thisWeek;
  const workPackageLastWeek = workPackages?.lastWeek || lastWeek;
  const generalConditionsProjectToDate = generalConditions?.projectToDate || {};
  const generalConditionsThisWeek = generalConditions?.thisWeek || {};
  const changeOrdersProjectToDate = changeOrders?.projectToDate || {};
  const changeOrdersThisWeek = changeOrders?.thisWeek || {};

  const ptdCostVar = fmtCostVariance(metrics?.costVarianceOverall);
  const ptdSchedVar = fmtVariance(metrics?.schedVarianceOverall);
  const twCostVar = fmtCostVariance(metrics?.costVarianceThisWeek);
  const twSchedVar = fmtVariance(metrics?.schedVarianceThisWeek);
  const blankVar = { text: '', color: 'text.secondary' };

  const lastWeekCostVar = fmtCostVariance(
    workPackageLastWeek
      ? (workPackageLastWeek.totalActual || 0) -
          (workPackageLastWeek.totalEarned || 0)
      : null
  );
  const lastWeekSchedVar = fmtVariance(
    workPackageLastWeek
      ? (workPackageLastWeek.totalEarned || 0) -
          (workPackageLastWeek.totalPlanned || 0)
      : null
  );

  const hdrSx = {
    fontSize: '11px',
    color: 'text.secondary',
    fontWeight: 500,
    py: 0.45,
    px: 0.9,
  };
  const cellSx = { fontSize: '12px', py: 0.45, px: 0.9 };
  const sectionSx = {
    fontSize: '11px',
    py: 0.45,
    px: 0.9,
    fontWeight: 700,
    color: 'text.secondary',
    bgcolor: 'action.hover',
    letterSpacing: '0.06em',
  };
  const pctSx = {
    fontSize: '11px',
    color: 'text.secondary',
    py: 0.15,
    px: 0.9,
  };
  const drillHdrSx = {
    fontSize: '11px',
    color: 'text.secondary',
    fontWeight: 600,
    py: 0.45,
    px: 0.9,
  };
  const drillCellSx = {
    fontSize: '12px',
    py: 0.45,
    px: 0.9,
    borderBottom: '0.5px solid',
    borderColor: 'divider',
  };
  const packageInfoGridSx = {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 1fr) 88px',
    gap: 1,
    alignItems: 'center',
  };
  const getVarianceHrs = (row) => {
    if (row.varianceHrs != null && Number.isFinite(Number(row.varianceHrs))) {
      return Number(row.varianceHrs);
    }
    return (Number(row.actualHours) || 0) - (Number(row.earnedHours) || 0);
  };
  const getVariancePct = (row, varianceHrs = getVarianceHrs(row)) => {
    if (row.variancePct != null && Number.isFinite(Number(row.variancePct))) {
      return Number(row.variancePct);
    }
    const earnedHours = Number(row.earnedHours) || 0;
    return earnedHours > 0 ? (varianceHrs / earnedHours) * 100 : null;
  };
  const getCostVarianceColor = (varianceHrs) => {
    if (varianceHrs > 0) return RED;
    if (varianceHrs < 0) return GREEN;
    return 'text.secondary';
  };
  const fmtVariancePct = (value) => {
    if (value == null || !Number.isFinite(value)) return '—';
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };
  const getVarianceFontWeight = (color, strong = false) => {
    if (color === RED) return strong ? 900 : 800;
    return strong ? 700 : 500;
  };

  const toggleRow = (rowKey) => {
    setExpandedRow((current) => (current === rowKey ? null : rowKey));
  };

  const DataRow = ({
    rowKey,
    label,
    planned,
    earned,
    actual,
    costVar,
    schedVar,
    bold,
  }) => (
    <TableRow
      hover
      onClick={() => toggleRow(rowKey)}
      sx={{
        cursor: 'pointer',
        ...(bold ? { bgcolor: 'action.hover' } : {}),
      }}
    >
      <TableCell sx={{ ...cellSx, fontWeight: bold ? 600 : 400 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            component="span"
            sx={{ color: 'text.secondary', fontSize: '11px', width: 8 }}
          >
            {expandedRow === rowKey ? '−' : '+'}
          </Box>
          {label}
        </Box>
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        {fmtNum(planned)}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        {fmtNum(earned)}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        {fmtNum(actual)}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          ...cellSx,
          color: costVar.color,
          fontWeight: getVarianceFontWeight(costVar.color),
        }}
      >
        {costVar.text}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          ...cellSx,
          color: schedVar.color,
          fontWeight: getVarianceFontWeight(schedVar.color),
        }}
      >
        {schedVar.text}
      </TableCell>
    </TableRow>
  );

  const SectionHeader = ({ label }) => (
    <TableRow>
      <TableCell colSpan={6} sx={sectionSx}>
        {label}
      </TableCell>
    </TableRow>
  );

  const StaticRow = ({
    label,
    planned,
    earned,
    actual,
    costVarText = '—',
    schedVarText = '—',
    bold,
  }) => (
    <TableRow sx={bold ? { bgcolor: 'action.hover' } : undefined}>
      <TableCell sx={{ ...cellSx, fontWeight: bold ? 600 : 400 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="span" sx={{ width: 8 }} />
          {label}
        </Box>
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        {fmtNum(planned)}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        {fmtNum(earned)}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        {fmtNum(actual)}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        {costVarText}
      </TableCell>
      <TableCell align="right" sx={cellSx}>
        {schedVarText}
      </TableCell>
    </TableRow>
  );

  const DrilldownRow = ({ rowKey, label, rows = [] }) => {
    const totals = rows.reduce(
      (acc, row) => ({
        budgetHours: acc.budgetHours + (row.budgetHours || 0),
        plannedHours: acc.plannedHours + (row.plannedHours || 0),
        earnedHours: acc.earnedHours + (row.earnedHours || 0),
        actualHours: acc.actualHours + (row.actualHours || 0),
        varianceHrs: acc.varianceHrs + getVarianceHrs(row),
      }),
      {
        budgetHours: 0,
        plannedHours: 0,
        earnedHours: 0,
        actualHours: 0,
        varianceHrs: 0,
      }
    );
    const totalsVariancePct =
      totals.earnedHours > 0
        ? (totals.varianceHrs / totals.earnedHours) * 100
        : null;

    const earnedColGridSx = {
      display: 'grid',
      gridTemplateColumns: '4.75rem minmax(0, 1fr)',
      columnGap: 1.25,
      width: '100%',
    };

    const earnedHoursCell = (hours, percentComplete, extraSx = {}) => (
      <TableCell align="right" sx={{ ...drillCellSx, ...extraSx }}>
        <Box
          sx={{
            ...earnedColGridSx,
            alignItems: 'baseline',
            whiteSpace: 'nowrap',
          }}
        >
          <Box
            sx={{
              fontSize: '11px',
              color: 'text.secondary',
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {percentComplete != null && Number.isFinite(percentComplete)
              ? `${percentComplete.toFixed(1)}%`
              : ''}
          </Box>
          <Box component="span" sx={{ textAlign: 'right' }}>
            {fmtNum(hours)}
          </Box>
        </Box>
      </TableCell>
    );

    return (
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, borderBottom: 0 }}>
          <Collapse in={expandedRow === rowKey} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.25, px: 0, bgcolor: 'action.hover' }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 0.75,
                  px: 0.9,
                }}
              >
                Package breakdown — {label}
              </Typography>

              {rows.length === 0 ? (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'text.secondary',
                    px: 0.9,
                  }}
                >
                  No package-level hours found for this period.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                  <Table
                    size="small"
                    stickyHeader
                    sx={{ width: '100%', bgcolor: 'background.paper' }}
                  >
                    <colgroup>
                      {columnWidths.map((width, index) => (
                        <col key={index} style={{ width }} />
                      ))}
                    </colgroup>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={drillHdrSx}>
                          <Box sx={packageInfoGridSx}>
                            <Box>Package</Box>
                            <Box sx={{ textAlign: 'right' }}>Budget</Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={drillHdrSx}>
                          Planned
                        </TableCell>
                        <TableCell align="right" sx={drillHdrSx}>
                          <Box
                            sx={{
                              ...earnedColGridSx,
                              alignItems: 'baseline',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Box sx={{ textAlign: 'right' }}>Earned %</Box>
                            <Box sx={{ textAlign: 'right' }}>Earned</Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={drillHdrSx}>
                          Actual
                        </TableCell>
                        <TableCell align="right" sx={drillHdrSx}>
                          Var Hrs
                        </TableCell>
                        <TableCell align="right" sx={drillHdrSx}>
                          Var %
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => {
                        const varianceHrs = getVarianceHrs(row);
                        const variancePct = getVariancePct(row, varianceHrs);

                        return (
                          <TableRow key={row.packageId}>
                            <TableCell sx={drillCellSx}>
                              <Box sx={packageInfoGridSx}>
                                <Box>{row.packageName}</Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  {fmtNum(row.budgetHours)}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={drillCellSx}>
                              {fmtNum(row.plannedHours)}
                            </TableCell>
                            {earnedHoursCell(
                              row.earnedHours,
                              row.percentComplete
                            )}
                            <TableCell align="right" sx={drillCellSx}>
                              {fmtNum(row.actualHours)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                ...drillCellSx,
                                color: getCostVarianceColor(varianceHrs),
                                fontWeight: getVarianceFontWeight(
                                  getCostVarianceColor(varianceHrs)
                                ),
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {fmtVariance(varianceHrs).text}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                ...drillCellSx,
                                color: getCostVarianceColor(varianceHrs),
                                fontWeight: getVarianceFontWeight(
                                  getCostVarianceColor(varianceHrs)
                                ),
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {fmtVariancePct(variancePct)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell sx={{ ...drillCellSx, fontWeight: 600 }}>
                          <Box sx={packageInfoGridSx}>
                            <Box>Total</Box>
                            <Box sx={{ textAlign: 'right' }}>
                              {fmtNum(totals.budgetHours)}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ ...drillCellSx, fontWeight: 600 }}
                        >
                          {fmtNum(totals.plannedHours)}
                        </TableCell>
                        {earnedHoursCell(totals.earnedHours, null, {
                          fontWeight: 600,
                        })}
                        <TableCell
                          align="right"
                          sx={{ ...drillCellSx, fontWeight: 600 }}
                        >
                          {fmtNum(totals.actualHours)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...drillCellSx,
                            color: getCostVarianceColor(totals.varianceHrs),
                            fontWeight: getVarianceFontWeight(
                              getCostVarianceColor(totals.varianceHrs),
                              true
                            ),
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {fmtVariance(totals.varianceHrs).text}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...drillCellSx,
                            color: getCostVarianceColor(totals.varianceHrs),
                            fontWeight: getVarianceFontWeight(
                              getCostVarianceColor(totals.varianceHrs),
                              true
                            ),
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {fmtVariancePct(totalsVariancePct)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const PctRow = ({ planned, earned, actual, budget }) => (
    <TableRow>
      <TableCell sx={pctSx} />
      <TableCell align="right" sx={pctSx}>
        {fmtPct(planned, budget)}
      </TableCell>
      <TableCell align="right" sx={pctSx}>
        {fmtPct(earned, budget)}
      </TableCell>
      <TableCell align="right" sx={pctSx}>
        {fmtPct(actual, budget)}
      </TableCell>
      <TableCell sx={pctSx} />
      <TableCell sx={pctSx} />
    </TableRow>
  );

  const ActualPctRow = ({ actual, budget }) => (
    <TableRow>
      <TableCell sx={pctSx} />
      <TableCell sx={pctSx} />
      <TableCell sx={pctSx} />
      <TableCell align="right" sx={pctSx}>
        {fmtPct(actual, budget)}
      </TableCell>
      <TableCell sx={pctSx} />
      <TableCell sx={pctSx} />
    </TableRow>
  );

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '0.5px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.25,
      }}
    >
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'text.secondary',
          mb: 1.5,
        }}
      >
        Labor Hours
      </Typography>

      <Box sx={{ overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{
            minWidth: 540,
            '& .MuiTableCell-root': {
              borderBottom: '0.5px solid',
              borderColor: 'divider',
            },
          }}
        >
          <colgroup>
            {columnWidths.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={hdrSx}>Period</TableCell>
              <TableCell align="right" sx={hdrSx}>
                Planned
              </TableCell>
              <TableCell align="right" sx={hdrSx}>
                Earned
              </TableCell>
              <TableCell align="right" sx={hdrSx}>
                Actual
              </TableCell>
              <TableCell align="right" sx={hdrSx}>
                Cost Var
              </TableCell>
              <TableCell align="right" sx={hdrSx}>
                Sched Var
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StaticRow
              label="Project total (budget)"
              planned={projectBudget}
              earned={null}
              actual={null}
              bold
            />

            <SectionHeader label="WORK PACKAGES" />
            <StaticRow
              label="Budget"
              planned={workPackageBudget}
              earned={null}
              actual={null}
            />

            <DataRow
              rowKey="projectToDate"
              label="Project to date"
              planned={workPackageProjectToDate?.totalPlanned}
              earned={workPackageProjectToDate?.totalEarned}
              actual={workPackageProjectToDate?.totalActual}
              costVar={ptdCostVar}
              schedVar={ptdSchedVar}
            />
            <DrilldownRow
              rowKey="projectToDate"
              label="Project to date"
              rows={packageDrilldowns.projectToDate}
            />
            <PctRow
              planned={workPackageProjectToDate?.totalPlanned}
              earned={workPackageProjectToDate?.totalEarned}
              actual={workPackageProjectToDate?.totalActual}
              budget={workPackageBudget}
            />

            <DataRow
              rowKey="thisWeek"
              label="This week"
              planned={workPackageThisWeek?.totalPlanned}
              earned={workPackageThisWeek?.totalEarned}
              actual={workPackageThisWeek?.totalActual}
              costVar={twCostVar}
              schedVar={twSchedVar}
            />
            <DrilldownRow
              rowKey="thisWeek"
              label="This week"
              rows={packageDrilldowns.thisWeek}
            />

            <DataRow
              rowKey="lastWeek"
              label="Last week"
              planned={workPackageLastWeek?.totalPlanned}
              earned={workPackageLastWeek?.totalEarned}
              actual={workPackageLastWeek?.totalActual}
              costVar={lastWeekCostVar}
              schedVar={lastWeekSchedVar}
            />
            <DrilldownRow
              rowKey="lastWeek"
              label="Last week"
              rows={packageDrilldowns.lastWeek}
            />

            <SectionHeader label="GENERAL CONDITIONS" />
            <StaticRow
              label="Budget"
              planned={generalConditionsBudget}
              earned={null}
              actual={null}
              costVarText=""
              schedVarText=""
            />
            <DataRow
              rowKey="generalConditionsProjectToDate"
              label="Project to date"
              planned={null}
              earned={null}
              actual={generalConditionsProjectToDate?.totalActual}
              costVar={blankVar}
              schedVar={blankVar}
            />
            <DrilldownRow
              rowKey="generalConditionsProjectToDate"
              label="General Conditions — Project to date"
              rows={packageDrilldowns.generalConditions?.projectToDate}
            />
            <ActualPctRow
              actual={generalConditionsProjectToDate?.totalActual}
              budget={generalConditionsBudget}
            />
            <DataRow
              rowKey="generalConditionsThisWeek"
              label="This week"
              planned={null}
              earned={null}
              actual={generalConditionsThisWeek?.totalActual}
              costVar={blankVar}
              schedVar={blankVar}
            />
            <DrilldownRow
              rowKey="generalConditionsThisWeek"
              label="General Conditions — This week"
              rows={packageDrilldowns.generalConditions?.thisWeek}
            />

            <SectionHeader label="CHANGE ORDERS" />
            <StaticRow
              label="Budget"
              planned={changeOrderBudget}
              earned={null}
              actual={null}
              costVarText=""
              schedVarText=""
            />
            <DataRow
              rowKey="changeOrdersProjectToDate"
              label="Project to date"
              planned={null}
              earned={null}
              actual={changeOrdersProjectToDate?.totalActual}
              costVar={blankVar}
              schedVar={blankVar}
            />
            <DrilldownRow
              rowKey="changeOrdersProjectToDate"
              label="Change Orders — Project to date"
              rows={packageDrilldowns.changeOrders?.projectToDate}
            />
            <ActualPctRow
              actual={changeOrdersProjectToDate?.totalActual}
              budget={changeOrderBudget}
            />
            <DataRow
              rowKey="changeOrdersThisWeek"
              label="This week"
              planned={null}
              earned={null}
              actual={changeOrdersThisWeek?.totalActual}
              costVar={blankVar}
              schedVar={blankVar}
            />
            <DrilldownRow
              rowKey="changeOrdersThisWeek"
              label="Change Orders — This week"
              rows={packageDrilldowns.changeOrders?.thisWeek}
            />
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default LaborHoursTable;
